<script setup lang="ts">
  import {
    BottomSheet,
    Button,
    ButtonAppearance,
    ButtonSize,
    Picture,
    PictureFormat,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { ref } from 'vue';
  import { SERP_SEARCH_EXPIRED_TEXTS } from '../config/texts';

  interface SerpSearchExpiredSheetEmits {
    (e: 'refresh'): void;
    (e: 'home'): void;
    (e: 'close'): void;
  }

  const emit = defineEmits<SerpSearchExpiredSheetEmits>();

  type SerpSearchExpiredAction = 'refresh' | 'home';

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  let action: SerpSearchExpiredAction | null = null;

  const closeWith = async (next: SerpSearchExpiredAction): Promise<void> => {
    action = next;
    await bottomSheetRef.value?.close();
  };

  const onClose = (): void => {
    if (action === 'refresh') emit('refresh');
    if (action === 'home') emit('home');
    emit('close');
  };
</script>

<template>
  <div
    class="serp-search-expired-sheet"
    data-testid="serp-search-expired-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="serp-search-expired-sheet__sheet"
      content-class="serp-search-expired-sheet__content"
      :animated="true"
      :icon-closable="true"
      @close="onClose"
    >
      <Picture
        path="illustrations/alert-hourglass.webp"
        :extensions="[PictureFormat.WEBP]"
        :high-resolution="true"
        :lazy="false"
        class="serp-search-expired-sheet__picture"
        fit="contain"
        aria-hidden="true"
        data-testid="serp-search-expired-sheet-picture"
      />

      <div class="serp-search-expired-sheet__text">
        <Typo
          class="serp-search-expired-sheet__title"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="serp-search-expired-sheet-title"
        >
          {{ SERP_SEARCH_EXPIRED_TEXTS.title }}
        </Typo>
        <Typo
          class="serp-search-expired-sheet__description"
          :variant="TypoVariant.BodyLRegular"
          tag="p"
          data-testid="serp-search-expired-sheet-description"
        >
          {{ SERP_SEARCH_EXPIRED_TEXTS.description }}
        </Typo>
      </div>

      <template #footer>
        <div class="serp-search-expired-sheet__actions">
          <Button
            class="serp-search-expired-sheet__button"
            :appearance="ButtonAppearance.Primary"
            :size="ButtonSize.L"
            data-testid="serp-search-expired-sheet-refresh"
            @click="closeWith('refresh')"
          >
            {{ SERP_SEARCH_EXPIRED_TEXTS.refresh }}
          </Button>
          <Button
            class="serp-search-expired-sheet__button"
            :appearance="ButtonAppearance.Tertiary"
            :size="ButtonSize.L"
            data-testid="serp-search-expired-sheet-home"
            @click="closeWith('home')"
          >
            <Typo
              :variant="TypoVariant.BodyLRegular"
              tag="span"
            >
              {{ SERP_SEARCH_EXPIRED_TEXTS.home }}
            </Typo>
          </Button>
        </div>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .serp-search-expired-sheet {
    display: contents;

    &__picture {
      flex-shrink: 0;
      height: 230px;
      width: 230px;
    }

    &__text {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
      text-align: center;
      width: 100%;
    }

    &__title,
    &__description {
      color: var(--color-text-primary);
      margin: 0;
    }

    &__actions {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      width: 100%;
    }

    &__button {
      width: 100%;
    }
  }
</style>

<style lang="scss">
  .serp-search-expired-sheet__sheet .serp-search-expired-sheet__content {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-m);
    padding: calc(var(--spacing-xs) + var(--spacing-xl) + var(--spacing-l)) var(--spacing-m) 0;
  }
</style>
